import { useMemo } from 'react';
import type { ContractTransactionImportSource } from './useTransactionBuilderStore';
import { useAddressBookEntriesByAddresses } from '@/hooks/organization/use-address-book-entries-by-addresses';

/**
 * Require saved addresses for manual builder fields and pasted raw calls.
 * WalletConnect and uploaded JSON may retain supplied addresses; their inputs
 * still use the save-first selector. This is a web form rule, not an authority
 * claim that the backend should trust from a client.
 */
export function useManualTransactionAddressValidation(
  addresses: string[],
  importSource?: ContractTransactionImportSource
) {
  const requiredAddresses = useMemo(
    () => (importSource ? [] : addresses),
    [addresses, importSource]
  );
  const { entryByAddress, isLoading, error } = useAddressBookEntriesByAddresses(
    requiredAddresses,
    {
      revalidateOnFocus: true,
      // A missing entry may be saved in another dialog. Recheck only unresolved
      // addresses so that the current form unlocks without retyping its values.
      refreshInterval: (entries) =>
        requiredAddresses.some(
          (address) =>
            !entries?.some(
              (entry) => entry.address.toLowerCase() === address.toLowerCase()
            )
        )
          ? 3000
          : 0,
    }
  );
  const missingAddresses = requiredAddresses.filter(
    (address) => !entryByAddress.has(address.toLowerCase())
  );
  const errorMessage = error
    ? 'The address book could not be checked. Please try again before continuing.'
    : !isLoading && missingAddresses.length > 0
      ? `Save this address in the address book before continuing: ${missingAddresses[0]}${missingAddresses.length > 1 ? ` (and ${missingAddresses.length - 1} more)` : ''}`
      : null;

  return {
    isValid: !isLoading && !error && missingAddresses.length === 0,
    isLoading,
    errorMessage,
  };
}
